// Cloudflare Turnstile on the two card payment screens (open paylink, payment
// request). The widget mounts here, its token is kept in this module, and the
// payment actions in web-app/actions.ts read it as `turnstile_token`. The API
// verifies the token once CARD_PAYMENT_TURNSTILE=required; until then it rides
// along unread. Tokens are single-use: the actions reset after any refusal so
// the retry carries a fresh one.
//
// Ported from apps/checkout/src/hooks/useTurnstile.ts; the module-level token
// is what lets a redux thunk read it without threading it through the store.

import React, { useEffect, useRef, useState } from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// The "card payments" widget, domain cuanto.app (covers staging.cuanto.app).
const SITE_KEY = "0x4AAAAAAFO535S_R_rYqOF1";
// Cloudflare's published always-passes test key, for local builds.
// https://developers.cloudflare.com/turnstile/troubleshooting/testing/
const TEST_SITE_KEY = "1x00000000000000000000AA";

// Pure over the channel: the build inlines process.env.EXPO_RELEASE_CHANNEL.
export function turnstileSiteKey(
  channel: string | undefined = process.env.EXPO_RELEASE_CHANNEL
): string {
  if (channel === "production" || channel === "staging") return SITE_KEY;
  return TEST_SITE_KEY;
}

interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": () => void;
      language: string;
      size: "normal" | "flexible" | "compact";
      theme: "auto" | "light" | "dark";
    }
  ): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let currentToken: string | null = null;
let currentWidgetId: string | null = null;

// The token the mounted widget last issued; null before it passes, after it
// expires, and after a reset.
export function getTurnstileToken(): string | null {
  return currentToken;
}

// After a refused payment: the token was spent, ask the widget for another.
export function resetTurnstile(): void {
  if (currentWidgetId && window.turnstile) {
    window.turnstile.reset(currentWidgetId);
  }
  currentToken = null;
}

function loadTurnstile(): Promise<TurnstileApi> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`
    );
    const script = existing ?? document.createElement("script");
    const done = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("turnstile script loaded without its API"));
    };
    script.addEventListener("load", done, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("turnstile script failed")),
      { once: true }
    );
    if (!existing) {
      script.src = SCRIPT_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });
}

type Props = {
  // Fires with the token when the check passes and with null when it lapses.
  onToken: (token: string | null) => void;
};

const containerStyle = {
  display: "flex",
  justifyContent: "center",
  padding: 16,
} as const;

// Renders the element Cloudflare draws its widget into: nothing for most
// people, a checkbox when it is unsure.
export function TurnstileWidget({ onToken }: Props) {
  const container = useRef<HTMLDivElement | null>(null);
  const [siteKey] = useState(turnstileSiteKey);

  useEffect(() => {
    const el = container.current;
    if (!el) return;
    let cancelled = false;
    const update = (token: string | null) => {
      currentToken = token;
      onToken(token);
    };
    loadTurnstile()
      .then((api) => {
        if (cancelled) return;
        currentWidgetId = api.render(el, {
          sitekey: siteKey,
          callback: update,
          "expired-callback": () => update(null),
          "error-callback": () => update(null),
          language: "es",
          size: "flexible",
          theme: "light",
        });
      })
      .catch(() => update(null));
    return () => {
      cancelled = true;
      if (currentWidgetId && window.turnstile) {
        window.turnstile.remove(currentWidgetId);
      }
      currentWidgetId = null;
      currentToken = null;
    };
    // onToken is a setter from the screen; the widget mounts once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={container} data-testid="turnstile" style={containerStyle} />;
}
